import { supabase } from '../lib/supabase'

export interface EmailData {
  to: string
  cc?: string
  subject: string
  body: string
  type: 'testspiel' | 'equipment' | 'bus' | 'general'
}

// Debug mode - set to true for development/testing
const DEBUG_MODE = false

// Email configuration — recipients come from env vars so real addresses
// aren't hardcoded in source; fallbacks match the addresses the club
// actually uses today, kept only as a safety net if an env var is missing.
const EMAIL_CONFIG = {
  // Default sender
  defaultSender: import.meta.env.VITE_EMAIL_DEFAULT_SENDER || 'app@fcfrick.ch',
  fallbackSender: import.meta.env.VITE_EMAIL_FALLBACK_SENDER || 'admin@all-answer.com',

  // Debug email (used when DEBUG_MODE is true) — must be set explicitly via env, no default
  debugEmail: import.meta.env.VITE_EMAIL_DEBUG || '',

  // Production email addresses
  production: {
    testspiel: {
      to: import.meta.env.VITE_EMAIL_TESTSPIEL_TO || 'testspiele@fcfrick.ch',
      cc: import.meta.env.VITE_EMAIL_TESTSPIEL_CC || 'armin.swiatek@fcfrick.ch'
    },
    equipment: {
      to: import.meta.env.VITE_EMAIL_EQUIPMENT_TO || 'material@fcfrick.ch',
      cc: import.meta.env.VITE_EMAIL_EQUIPMENT_CC || 'thomas.keller@fcfrick.ch'
    },
    bus: {
      to: import.meta.env.VITE_EMAIL_BUS_TO || 'bus@fcfrick.ch',
      cc: undefined // No CC for bus reservations
    }
  }
}

function getEmailAddresses(type: 'testspiel' | 'equipment' | 'bus', userEmail: string) {
  if (DEBUG_MODE) {
    return {
      to: EMAIL_CONFIG.debugEmail,
      cc: userEmail !== EMAIL_CONFIG.debugEmail ? userEmail : undefined
    }
  }

  const config = EMAIL_CONFIG.production[type]
  const addresses = {
    to: config.to,
    cc: undefined as string | undefined
  }

  // Build CC list, avoiding duplicates and ensuring user gets copy
  const ccList: string[] = []
  
  // Add config CC if exists and different from TO
  if (config.cc && config.cc !== addresses.to) {
    ccList.push(config.cc)
  }
  
  // ALWAYS add user email to CC if provided and different from TO
  if (userEmail && userEmail !== addresses.to) {
    // Check if user email is already in CC list
    if (!ccList.includes(userEmail)) {
      ccList.push(userEmail)
    }
  }
  
  // Always add fallback admin to ensure someone gets copy
  const adminEmail = EMAIL_CONFIG.fallbackSender
  if (!ccList.includes(adminEmail) && addresses.to !== adminEmail && userEmail !== adminEmail) {
    ccList.push(adminEmail)
  }
  
  // Clean and validate CC list
  if (ccList.length > 0) {
    // Remove any empty or invalid emails
    const validCCs = ccList.filter(email => email && email.trim().length > 0 && email.includes('@'))
    addresses.cc = validCCs.join(',')
  }

  console.log('📧 Email routing configured:', {
    type,
    to: addresses.to,
    cc: addresses.cc,
    userEmail,
    ccCount: ccList.length
  })

  return addresses
}

export async function sendEmail(emailData: EmailData): Promise<{ success: boolean; error?: string }> {
  try {
    // Check if running locally (development mode)
    const isLocalDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    
    if (isLocalDevelopment) {
      // Local development - just log the email instead of sending
      console.log('📧 LOCAL DEV MODE - Email would be sent:', {
        to: emailData.to,
        cc: emailData.cc,
        subject: emailData.subject,
        type: emailData.type,
        body: emailData.body.substring(0, 200) + '...'
      })
      
      // Simulate successful send for development
      return { success: true }
    }

    // Production - Use Netlify function for SendGrid.
    // The function requires an approved, logged-in caller and derives the
    // recipients server-side from `type`; the to/cc below are ignored by it.
    const { data: sessionData } = await supabase.auth.getSession()
    const token = sessionData?.session?.access_token
    if (!token) {
      return { success: false, error: 'Nicht angemeldet' }
    }

    const response = await fetch('/.netlify/functions/send-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(emailData)
    })

    const result = await response.json()

    if (!response.ok) {
      throw new Error(result.error || 'Failed to send email')
    }

    console.log('📧 Email sent successfully via Netlify + SendGrid')
    return { success: true }

  } catch (error) {
    console.error('❌ Email sending error:', error)
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error occurred' 
    }
  }
}

/**
 * Meldet ein Support-Ticket an das Postfach ihrer Kategorien.
 *
 * Schickt bewusst NUR die Ticket-ID: Empfänger, Betreff und Text baut die
 * Function serverseitig aus dem gespeicherten Ticket. Der Client kann damit
 * weder eine Adresse noch einen Inhalt vorgeben.
 *
 * Anders als sendEmail() gibt es hier keine localhost-Abkürzung — der
 * Serverpfad soll auch lokal echt durchlaufen. Dass dabei nichts rausgeht,
 * stellt der Guard der Function sicher (kein Produktionskontext, und ohne
 * SENDGRID_API_KEY ohnehin nur simuliert).
 */
export async function sendSupportTicketEmail(
  ticketId: string,
): Promise<{ success: boolean; simulated?: boolean; error?: string }> {
  try {
    const { data: sessionData } = await supabase.auth.getSession()
    const token = sessionData?.session?.access_token
    if (!token) return { success: false, error: 'Nicht angemeldet' }

    const response = await fetch('/.netlify/functions/send-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ type: 'support', ticketId }),
    })

    const result = await response.json().catch(() => ({}))
    if (!response.ok) {
      return { success: false, error: result?.error || 'Benachrichtigung fehlgeschlagen' }
    }
    return { success: true, simulated: !!result?.simulated }
  } catch (error) {
    console.error('❌ Support notification error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unbekannter Fehler',
    }
  }
}

// Helper functions for different email types
export function createTestspielEmail(testspielData: any, userEmail: string): EmailData {
  const formattedDate = new Date(testspielData.date).toLocaleDateString('de-DE', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  })

  const subject = `Neue Testspielvereinbarung - ${testspielData.team} vs. ${testspielData.opponent}`
  
  const body = `Hallo FC Frick Team,

ein neues Testspiel wurde über die App erstellt und benötigt eure Bestätigung:

SPIELDETAILS:
Team: ${testspielData.team}
Gegner: ${testspielData.opponent}
Datum: ${formattedDate}
Anstoss: ${testspielData.time} Uhr
Ort: Vereinsplatz FC Frick

TRAINER/VERANTWORTLICHER:
${testspielData.trainerName}

${testspielData.notes ? `ZUSÄTZLICHE INFORMATIONEN:\n${testspielData.notes}` : ''}

Bitte bestätigt das Testspiel oder meldet euch bei Fragen.

Sportliche Grüsse
FC Frick App

---
Diese E-Mail wurde automatisch generiert.`

  const addresses = getEmailAddresses('testspiel', userEmail)

  return {
    to: addresses.to,
    cc: addresses.cc,
    subject,
    body,
    type: 'testspiel'
  }
}

export function createEquipmentEmail(orderData: any, userEmail: string): EmailData {
  const orderItems = orderData.items.map((item: any) => 
    `- ${item.name}${item.size ? ` (${item.size})` : ''} x ${item.quantity}`
  ).join('\n')
  
  const subject = `Neue Equipment-Bestellung - ${orderData.team}`
  
  const body = `Hallo FC Frick Team,

eine neue Equipment-Bestellung wurde über die App erstellt:

BESTELLDETAILS:
Bestellt von: ${orderData.orderedBy}
Team: ${orderData.team}
Trainer/Verantwortlicher: ${orderData.trainerName}
Gewünschte Lieferung: ${orderData.deliveryMonth}

BESTELLTE ARTIKEL:
${orderItems}

${orderData.notes ? `ZUSÄTZLICHE INFORMATIONEN:\n${orderData.notes}` : ''}

Bitte bearbeitet die Bestellung über das Admin-Panel.

Sportliche Grüsse
FC Frick App

---
Diese E-Mail wurde automatisch generiert.`

  const addresses = getEmailAddresses('equipment', userEmail)

  return {
    to: addresses.to,
    cc: addresses.cc,
    subject,
    body,
    type: 'equipment'
  }
}

export function createBusEmail(reservationData: any, userEmail: string): EmailData {
  const formattedDate = new Date(reservationData.date).toLocaleDateString('de-DE', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  })
  
  const subject = `Neue Bus-Reservierung - ${reservationData.team}`
  
  const body = `Hallo FC Frick Team,

eine neue Bus-Reservierung wurde über die App erstellt:

RESERVIERUNGSDETAILS:
Team: ${reservationData.team}
Datum: ${formattedDate}
Abfahrt: ${reservationData.startTime} Uhr
Rückfahrt: ${reservationData.endTime} Uhr${reservationData.returnDate ? ` (am ${new Date(reservationData.returnDate).toLocaleDateString('de-DE')})` : ''}
Ziel: ${reservationData.destination}
Zweck: ${reservationData.purpose}

FAHRER & KONTAKT:
Fahrer: ${reservationData.driver}
Kontakt-Telefon: ${reservationData.contactPhone}

${reservationData.notes ? `ZUSÄTZLICHE INFORMATIONEN:\n${reservationData.notes}` : ''}

Bitte bestätigt die Reservierung oder meldet euch bei Fragen.

Sportliche Grüsse
FC Frick App

---
Diese E-Mail wurde automatisch generiert.`

  const addresses = getEmailAddresses('bus', userEmail)

  return {
    to: addresses.to,
    cc: addresses.cc,
    subject,
    body,
    type: 'bus'
  }
}
